import {
  AnalyticsHelper,
  Formatter,
  requestBlogsSSR,
  requestCMSProductionPhotoSSR,
  requestShowBrandOnlyShowSSR,
  requestTestimonialsSSR,
  requestVariantSSR,
} from "@with-nx/hooks-n-helpers";
import { ReusableSection, Rule } from "@with-nx/simple-ui/atoms";
import {
  CatalogLayout,
  GridBlog,
  GridStepScroller,
  ImageGallery,
  ReusableSimpleHeroSection,
  TestimonialQuotes,
} from "@with-nx/simple-ui/organisms";
import { ShowDataStatic } from "@with-nx/static";
import { Row } from "antd";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { Box } from "simple-effing-primitive-layout";

import { ShowsTab } from "../../../../components/w/shows/ShowsTab";

export const getServerSideProps = async (context) => {
  const { variant } = context.params;

  const fallbackSlugs = [
    "broadway-media-difference",
    "ensuring-your-success-in-theatre-making",
    "digital-scenery-yes-you-can",
  ];

  const showVariant = !ShowDataStatic.isShowsEmpty()
    ? ShowDataStatic.getShowBySlug(variant) ||
      (await requestVariantSSR(variant))
    : await requestVariantSSR(variant);

  if (!showVariant) {
    return {
      notFound: true,
    };
  }

  const [testimonials, fallback] = await Promise.all([
    requestTestimonialsSSR({ per: 10 }),
    requestBlogsSSR({
      filter: {
        slug: fallbackSlugs,
      },
    }),
  ]);

  const [blogs, images, isOnlyShow] = showVariant
    ? await Promise.all([
        requestBlogsSSR({
          filter: {
            showVariantTags: [showVariant?.id.toString()],
            showBrandTags: [showVariant?.showBrand?.id.toString()],
          },
        }),
        requestCMSProductionPhotoSSR({
          filter: {
            showVariantTags: [showVariant?.id?.toString()],
            showBrandTags: [showVariant.showBrand?.id?.toString()],
          },
        }),
        requestShowBrandOnlyShowSSR(showVariant?.showBrand?.slug),
      ])
    : [undefined, undefined, undefined];

  return {
    props: {
      resolvedUrl: context.resolvedUrl,
      showVariant,
      testimonials: testimonials?.data,
      blogs: blogs?.data,
      fallbackSlugs,
      fallback: fallback?.data,
      images: images?.data,
      isOnlyShow,
    },
  };
};

export default function Page({
  resolvedUrl,
  showVariant,
  testimonials,
  blogs,
  fallbackSlugs,
  fallback,
  images,
  isOnlyShow,
}) {
  const router = useRouter();

  useEffect(() => {
    AnalyticsHelper.collect(showVariant?.id);
  }, [showVariant?.id]);

  useEffect(() => {
    if (
      testimonials?.length === 0 &&
      blogs?.length === 0 &&
      images?.length === 0
    ) {
      if (showVariant?.scenicProjections?.length > 0) {
        router.push(
          `/shows/${router.query?.brand}/${router.query?.variant}/scenic-projections`
        );
      } else if (showVariant?.showBrand?.dropsCount > 0) {
        router.push(
          `/shows/${router.query?.brand}/${router.query?.variant}/backdrop-library`
        );
      } else if (showVariant?.choreoGuide) {
        router.push(
          `/shows/${router.query?.brand}/${router.query?.variant}/choreography-guides`
        );
      }
    }
  }, [
    router,
    showVariant?.scenicProjections?.length,
    showVariant?.showBrand?.dropsCount,
    showVariant?.choreoGuide,
    testimonials?.length,
    blogs.loading,
    blogs?.length,
    images?.length,
  ]);

  let shouldHideLogo =
    !showVariant?.scenicProjections.length && !showVariant?.choreoGuide;

  if (showVariant?.forceShowLogo) {
    shouldHideLogo = false;
  }

  return (
    <CatalogLayout
      footerNoAngle
      transparentFixedNavBar
      title={
        showVariant?.metaTitle ? showVariant?.metaTitle : showVariant?.title
      }
      description={
        showVariant?.metaDescription
          ? showVariant?.metaDescription
          : showVariant?.marketingDescription
      }
      resolvedUrl={resolvedUrl}
    >
      <ReusableSimpleHeroSection
        image={
          showVariant?.attachments?.find((i) => i.name === "image")?.blob
            .signedUrl
        }
        press={() => {
          router.push(`/shows/${router.query?.brand}`);
        }}
        title={showVariant?.title}
        description={showVariant?.marketingDescription}
        show={{
          name: showVariant?.title,
          image: shouldHideLogo
            ? undefined
            : showVariant?.attachments?.find((i) => i.name === "logo")?.blob
                .signedUrl,
          color: showVariant?.logoColor || showVariant?.showBrand?.logoColor,
        }}
      />

      <div className="container">
        <ShowsTab
          waitlist={showVariant?.scenicProjections?.length === 0}
          empty={false}
          loading={isOnlyShow.loading}
          showVariant={showVariant}
          onlyShow={isOnlyShow.only}
          currentTab="Overview"
        />
      </div>

      {blogs?.length > 0 || fallback?.length > 0 ? (
        <ReusableSection>
          <div className="container">
            <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
              Stories
            </Rule>
            <Row gutter={[36, 18]}>
              <GridStepScroller
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                items={[
                  ...(blogs.blogs?.filter(
                    (b) => !fallbackSlugs.includes(b.slug)
                  ) || []),
                  ...(fallback || []),
                ].map((blog, b) => {
                  return (
                    <GridBlog
                      key={blog._id}
                      date={blog.date}
                      description={blog.description}
                      image={Formatter.compressed(blog.coverImage, 256, true)}
                      title={blog.title}
                      url={`/blog/${blog.slug}`}
                      transparent={false}
                      properties={{
                        height: "100%",
                      }}
                    />
                  );
                })}
              />
            </Row>
          </div>
        </ReusableSection>
      ) : undefined}
      {images?.length >= 5 ? (
        <ReusableSection>
          <div className="container">
            <Rule rule="hl" color="var(--font1)" bottom={24} display="block">
              {showVariant?.title}{" "}
              <span style={{ color: "var(--primary)" }}>Production Photos</span>
            </Rule>
          </div>
          <ImageGallery
            images={images?.map((image) => ({
              src: Formatter.image(image.image),
              meta: {
                copyright: image.copyrightClaim,
                organization: image.organizationName,
                name: image.name,
                date: image.productionDate,
              },
            }))}
            per={3}
          />
        </ReusableSection>
      ) : undefined}

      {testimonials?.length > 0 ? (
        <ReusableSection>
          <div className="container mx-auto max-w-7xl">
            <Rule parse="!dt d:block mb:24 c:?font1 ta:center" tag="h2">
              Trusted by our Theatre Community
            </Rule>
          </div>

          <Box parse="mh:200 mb:32">
            <TestimonialQuotes testimonials={testimonials} />
          </Box>
        </ReusableSection>
      ) : undefined}
    </CatalogLayout>
  );
}
